// Page-wide answers to four questions: is a show running, who is hosting it,
// what have they verified so far tonight, and has After Dark been called.
//
// A module-level store rather than component state, because pieces of UI in
// several different corners need the same answers: the launcher button in the
// header, the hosts strip under it, the glow on the card's own squares, and the
// theme itself in App.js. One store means one set of socket listeners and one
// poll no matter how much UI is on screen -- and, critically, no second socket
// connection (see src/liveSocket.js: the live-viewer number is the connection
// count, so a second connection would visibly inflate it for everybody).
//
// EVERYTHING IS ESTABLISHED ON MOUNT, from two independent sources at once:
//
//   * the socket, which pushes a full snapshot the instant it connects, and
//   * plain HTTP, which needs no handshake and still works on a network that
//     quietly drops the socket's non-standard port.
//
// Whichever answers first wins. The socket takes charge for good once it is
// connected and stays in charge; HTTP takes back over by itself if the socket
// ever drops. Nothing is set twice with the same value (see setState), so the
// two sources agreeing costs nothing and can never flicker.
//
// This is the fix for a real failure: the ONLY source of "a show is on" used to
// be a socket event, and the launcher was additionally hidden whenever the
// hosts endpoint answered with an empty list -- which it does the moment nobody
// has touched the admin panel for three minutes. Somebody opening the page in
// the quiet stretch between two confirmations got no LIVE NOW marker and an
// empty verified list until a host happened to tick another square.
import { useEffect, useState } from 'react';
import getLiveSocket, { normalizeTile, LIVE_SNAPSHOT_URL } from '../../liveSocket';

const HOSTS_URL = 'https://wanshow.bingo/api/live-status/hosts';
const LIVE_POLL_MS = 30000;    // while a show is on
const IDLE_POLL_MS = 60000;    // ...and a slower heartbeat when it is not

// Who is hosting is a nice-to-have, not a gate. A hosts endpoint that 404s,
// times out or returns nonsense means we do not KNOW tonight's hosts, which is
// not the same as knowing a show is not running -- and it is certainly not a
// reason to hide the squares the show has already verified. The strip simply
// draws no faces when it has no faces to draw.
const INITIAL = {
  live: false,
  episodeId: null,
  tiles: [],                        // newest first, de-duplicated by square text
  hosts: [],
  hostsKnown: false,                // true only after the endpoint answered
  afterDark: { on: false, at: null },
  ready: false,                     // true once ANY source has answered
};

let state = INITIAL;
const listeners = new Set();
let handlers = null;      // socket listeners we attached, so we remove only ours
let pollTimer = null;
let pollEvery = 0;
let hostsFetching = false;
let snapshotFetching = false;
let bootstrapped = false;

// The socket is the live path; HTTP is the safety net. Once the socket has
// spoken AND is still connected, an HTTP reply is old news by definition, and
// dropping it is exactly what stops the two fighting over the same pixels.
let socketSpoke = false;

function socketInCharge() {
  if (!socketSpoke) return false;
  try { return !!getLiveSocket().connected; } catch (e) { return false; }
}

// ---------------------------------------------------------------- state -----

function sameTiles(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].key !== b[i].key) return false;
    if (a[i].confirmedAt !== b[i].confirmedAt) return false;
    if (a[i].tileText !== b[i].tileText) return false;
  }
  return true;
}

function sameHosts(a, b) {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].id !== b[i].id || a[i].name !== b[i].name || a[i].avatarUrl !== b[i].avatarUrl) return false;
  }
  return true;
}

/**
 * Apply a patch, but only tell anybody if something actually changed. Two
 * sources answering the same question with the same answer -- which is the
 * normal case on a healthy connection -- must cost exactly one render, not two.
 */
function setState(patch) {
  const next = { ...state, ...patch };

  // Keep the old references when the contents match, so React's own bail-outs
  // and any useMemo downstream see genuinely unchanged values.
  if (next.tiles !== state.tiles && sameTiles(next.tiles, state.tiles)) next.tiles = state.tiles;
  if (next.hosts !== state.hosts && sameHosts(next.hosts, state.hosts)) next.hosts = state.hosts;
  if (next.afterDark !== state.afterDark
      && next.afterDark.on === state.afterDark.on
      && next.afterDark.at === state.afterDark.at) {
    next.afterDark = state.afterDark;
  }

  if (next.live === state.live
      && next.episodeId === state.episodeId
      && next.tiles === state.tiles
      && next.hosts === state.hosts
      && next.hostsKnown === state.hostsKnown
      && next.afterDark === state.afterDark
      && next.ready === state.ready) {
    return;
  }

  state = next;
  listeners.forEach((notify) => {
    // One component throwing must not stop the others from updating.
    try { notify(state); } catch (e) { /* ignore */ }
  });
}

// --------------------------------------------------------------- times ------
// The socket server hands us `YYYY-MM-DD HH:MM:SS` in its own timezone
// (America/Chicago) on every path -- the live `tileConfirmed` event builds the
// string by hand, the database pool runs with `dateStrings: true`, and the HTTP
// snapshot serialises the same records. Handing that to `new Date()` would have
// every browser re-interpret show time as its own local time, so the clock is
// read straight out of the string instead. ISO instants are still handled in
// case that ever changes.

const BARE_DATETIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/;

export function formatConfirmedTime(value) {
  if (!value) return '';
  const raw = String(value).trim();

  const bare = BARE_DATETIME.exec(raw);
  if (bare) return to12Hour(Number(bare[4]), bare[5]);

  const parsed = Date.parse(raw);
  if (Number.isNaN(parsed)) return '';
  const d = new Date(parsed);
  return to12Hour(d.getHours(), String(d.getMinutes()).padStart(2, '0'));
}

function to12Hour(hours24, minutes) {
  const suffix = hours24 >= 12 ? 'PM' : 'AM';
  const hour = hours24 % 12 === 0 ? 12 : hours24 % 12;
  return `${hour}:${minutes} ${suffix}`;
}

/** Sort key only -- never rendered, so a rough ordering is enough. */
function sortKey(value) {
  if (!value) return 0;
  const raw = String(value).trim();
  const bare = BARE_DATETIME.exec(raw);
  if (bare) {
    return Number(
      bare[1] + bare[2] + bare[3] + bare[4] + bare[5] + (bare[6] || '00')
    );
  }
  const parsed = Date.parse(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
}

// --------------------------------------------------------------- tiles ------

function toEntry(raw) {
  const text = raw && raw.tileText;
  if (!text) return null;
  const key = normalizeTile(text);
  if (!key) return null;
  return {
    key,
    tileText: String(text),
    category: raw.category ? String(raw.category) : '',
    confirmedAt: raw.confirmedAt || null,
  };
}

// The same square can arrive twice -- the snapshot overlaps the per-square
// events, HTTP overlaps the socket, and a host can un-confirm and re-confirm
// while fixing a mistake. Later entries win, so the freshest timestamp is kept.
function collapse(entries) {
  const byKey = new Map();
  entries.forEach((entry) => { if (entry) byKey.set(entry.key, entry); });
  return Array.from(byKey.values()).sort(
    (a, b) => sortKey(b.confirmedAt) - sortKey(a.confirmedAt)
  );
}

// ------------------------------------------------------------ snapshots -----

function toAfterDark(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return { on: !!raw.on, at: raw.at || null };
}

/**
 * A full picture of tonight, from either source. Identical shape on both, on
 * purpose: the socket's `confirmedTiles` event and GET /api/live-snapshot are
 * serialised by the same function on the server.
 */
function applySnapshot(data) {
  const live = !!(data && data.live);
  const rows = live && data && Array.isArray(data.tiles) ? data.tiles : [];
  const patch = {
    live,
    episodeId: (data && data.episodeId) || null,
    tiles: collapse(rows.map(toEntry)),
    // Last night's hosts must not linger over tonight's dark card.
    hosts: live ? state.hosts : [],
    hostsKnown: live ? state.hostsKnown : false,
    ready: true,
  };

  const afterDark = toAfterDark(data && data.afterDark);
  // After Dark belongs to the show. When there is no show the lights are up,
  // whatever anybody last said.
  if (!live) patch.afterDark = { on: false, at: null };
  else if (afterDark) patch.afterDark = afterDark;

  setState(patch);
  syncPolling();
}

// -------------------------------------------------------------- sockets -----

function bindSocket() {
  if (handlers) return;
  const socket = getLiveSocket();

  handlers = {
    // Everything confirmed this session plus the state of the lights, sent on
    // connect and again whenever a show starts or ends.
    confirmedTiles: (data) => {
      socketSpoke = true;
      applySnapshot(data);
    },

    // Sent to everybody the moment the panel calls After Dark or takes it back,
    // and to each socket on connect, so arriving late still gets you the theme.
    afterDark: (data) => {
      socketSpoke = true;
      const next = toAfterDark(data) || { on: false, at: null };
      setState({ afterDark: next, ready: true });
    },

    tileConfirmed: (data) => {
      const entry = toEntry(data);
      if (!entry) return;
      // A square can only be confirmed during a show. If we somehow had not
      // worked out that one was running, this settles it.
      setState({ live: true, ready: true, tiles: collapse(state.tiles.concat([entry])) });
      syncPolling();
    },

    // An admin can take a confirmation back after tapping the wrong square.
    tileUnconfirmed: (data) => {
      const key = normalizeTile(data && data.tileText);
      if (!key) return;
      setState({ tiles: state.tiles.filter((t) => t.key !== key) });
    },
  };

  Object.keys(handlers).forEach((event) => socket.on(event, handlers[event]));
}

function unbindSocket() {
  if (!handlers) return;
  const socket = getLiveSocket();
  // Shared connection: our listeners come off, the connection stays up for the
  // card and the toaster.
  Object.keys(handlers).forEach((event) => socket.off(event, handlers[event]));
  handlers = null;
}

// ----------------------------------------------------------------- http -----

/** The socket's snapshot, as one plain GET. No handshake, no waiting. */
function fetchSnapshot() {
  if (snapshotFetching) return;
  snapshotFetching = true;

  fetch(LIVE_SNAPSHOT_URL, { credentials: 'omit', cache: 'no-store' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      if (socketInCharge()) return;      // the socket got there first; it wins
      applySnapshot(data);
    })
    .catch(() => {
      // The socket is the primary path and may well be fine. Nothing to do.
    })
    .then(() => { snapshotFetching = false; }, () => { snapshotFetching = false; });
}

function toHost(raw, index) {
  if (!raw) return null;
  const name = String(raw.name || raw.username || raw.displayName || '').trim();
  if (!name) return null;
  // Only an https URL ever reaches an <img src>; anything else (a relative
  // path, a data: or javascript: value from a malformed record) falls back to
  // the initial circle.
  const avatar = typeof raw.avatarUrl === 'string' && /^https:\/\//i.test(raw.avatarUrl)
    ? raw.avatarUrl
    : '';
  return { id: String(raw.id || `${name}:${index}`), name, avatarUrl: avatar };
}

/**
 * Who is verifying right now -- and, while the socket is silent, whether a show
 * is running at all: this endpoint knows, and it answers over ordinary HTTPS on
 * the ordinary port.
 */
function fetchHosts() {
  if (hostsFetching) return;
  // A hidden tab need not keep polling, but it absolutely must do the first
  // fetch: a page opened in a background tab has to be right when it is looked at.
  if (state.ready && typeof document !== 'undefined' && document.hidden) return;
  hostsFetching = true;

  fetch(HOSTS_URL, { credentials: 'omit', cache: 'no-store' })
    .then((res) => {
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.json();
    })
    .then((data) => {
      const live = !!(data && data.live);
      const rows = data && Array.isArray(data.hosts) ? data.hosts : [];
      const hosts = rows.map(toHost).filter(Boolean).slice(0, 12);

      if (socketInCharge()) {
        // The socket owns "is a show on"; this call only ever adds faces.
        setState(state.live ? { hosts, hostsKnown: true } : { hosts: [], hostsKnown: false });
      } else if (live) {
        setState({
          live: true,
          episodeId: (data && data.episodeId) || null,
          hosts,
          hostsKnown: true,
          ready: true,
        });
      } else {
        setState({
          live: false,
          episodeId: null,
          tiles: [],
          hosts: [],
          hostsKnown: false,
          afterDark: { on: false, at: null },
          ready: true,
        });
      }
      syncPolling();
    })
    .catch(() => {
      // 404, a network blip, HTML where JSON was promised: all the same answer.
      // We do not know tonight's hosts, so the strip draws no faces -- but
      // nothing else is hidden over it.
      setState({ hosts: [], hostsKnown: false });
    })
    .then(() => { hostsFetching = false; }, () => { hostsFetching = false; });
}

/** Everything a page that has just opened needs, asked for at once. */
function bootstrap() {
  if (bootstrapped) return;
  bootstrapped = true;
  fetchSnapshot();
  fetchHosts();
}

function poll() {
  fetchHosts();
  // Only worth asking while the socket is not the one telling us; when it is,
  // it has already pushed anything this would return.
  if (!socketInCharge()) fetchSnapshot();
}

function syncPolling() {
  const wanted = listeners.size > 0 ? (state.live ? LIVE_POLL_MS : IDLE_POLL_MS) : 0;
  if (wanted === pollEvery) return;
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  pollEvery = wanted;
  if (wanted) pollTimer = setInterval(poll, wanted);
}

function onVisibilityChange() {
  if (!document.hidden) poll();
}

// ----------------------------------------------------------------- hook -----

export default function useLiveShow() {
  const [snapshot, setSnapshot] = useState(state);

  useEffect(() => {
    listeners.add(setSnapshot);
    if (listeners.size === 1) {
      bindSocket();
      bootstrap();
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
    setSnapshot(state);          // catch up on anything that arrived pre-mount
    syncPolling();

    return () => {
      listeners.delete(setSnapshot);
      if (listeners.size === 0) {
        unbindSocket();
        document.removeEventListener('visibilitychange', onVisibilityChange);
        syncPolling();
      }
    };
  }, []);

  return snapshot;
}

/**
 * A show is on air. That is the whole rule, deliberately: what the hosts have
 * verified is worth reading whether or not anybody happens to be sat in the
 * panel this minute.
 */
export function shouldShowLauncher(s) {
  return !!(s && s.live);
}

/**
 * Same rule. The strip's job is to say LIVE NOW; the faces are a credit it adds
 * when it has them, not a condition for showing up.
 */
export function shouldShowHosts(s) {
  return !!(s && s.live);
}
